import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { formatCalendarDate } from '../../../../utils/calendarDate';

const headerCellSx = {
  fontSize: '10px',
  fontWeight: 600,
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  py: 0.75,
  whiteSpace: 'nowrap',
  borderBottom: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  position: 'sticky',
  top: 0,
  zIndex: 2,
};

const bodyCellSx = {
  fontSize: '12px',
  py: 0.75,
  borderBottom: '0.5px solid',
  borderColor: 'divider',
  verticalAlign: 'middle',
};

const formatHours = (value) =>
  `${Math.round((Number(value) || 0) * 10) / 10}`.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ','
  );

const formatVariancePct = (value) =>
  value == null || Number.isNaN(Number(value)) ? '-' : `${Number(value)}%`;

const formatDate = (value) =>
  formatCalendarDate(value, {
    month: 'short',
    day: 'numeric',
    year: '2-digit',
  });

const formatDateRange = (start, finish) => {
  const formattedStart = formatDate(start);
  const formattedFinish = formatDate(finish);

  if (formattedStart && formattedFinish) {
    return `${formattedStart} - ${formattedFinish}`;
  }
  return formattedStart || formattedFinish || '-';
};

const formatPackageName = (pkg) => {
  if (pkg.autodeskName && pkg.name) return `${pkg.autodeskName} - ${pkg.name}`;
  return pkg.autodeskName || pkg.name || '-';
};

const getVarianceColor = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue === 0) {
    return 'text.primary';
  }
  return numericValue > 0 ? 'warning.main' : 'success.main';
};

const WorkPackageProgressTable = ({
  packages,
  showPlannedDates = false,
  showActualHours = false,
  showVariance = false,
}) => {
  const columns = [
    { key: 'package', label: 'Package', align: 'left' },
    { key: 'pm', label: 'PM', align: 'left', width: 130 },
    { key: 'status', label: 'Status', align: 'left', width: 150 },
  ];

  if (showPlannedDates) {
    columns.push({
      key: 'plannedDates',
      label: 'Planned Dates',
      align: 'left',
      width: 170,
    });
  }

  columns.push(
    { key: 'shopHrs', label: 'Shop Hrs', align: 'right', width: 90 },
    { key: 'fieldHrs', label: 'Field Hrs', align: 'right', width: 90 },
    { key: 'totalHrs', label: 'Total Budget Hrs', align: 'right', width: 120 }
  );

  if (showActualHours) {
    columns.push({
      key: 'actualHrs',
      label: 'Actual Hrs',
      align: 'right',
      width: 95,
    });
  }

  if (showVariance) {
    columns.push(
      {
        key: 'varianceHrs',
        label: 'Variance Hrs',
        align: 'right',
        width: 105,
      },
      {
        key: 'variancePct',
        label: 'Variance %',
        align: 'right',
        width: 95,
      }
    );
  }

  return (
    <TableContainer sx={{ maxHeight: 420, bgcolor: 'background.paper' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                align={column.align}
                sx={{ ...headerCellSx, width: column.width }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {(packages || []).map((pkg, index) => (
            <TableRow
              key={pkg.id}
              sx={{
                height: 52,
                bgcolor: index % 2 === 0 ? 'background.paper' : 'action.hover',
              }}
            >
              <TableCell sx={bodyCellSx}>
                <Typography
                  sx={{ fontSize: '12px', fontWeight: 500, lineHeight: 1.3 }}
                >
                  {formatPackageName(pkg)}
                </Typography>
              </TableCell>
              <TableCell sx={bodyCellSx}>
                {pkg.pmDisplayName || 'Unassigned'}
              </TableCell>
              <TableCell sx={bodyCellSx}>
                {pkg.currentTrackingStatusName || '-'}
              </TableCell>
              {showPlannedDates && (
                <TableCell sx={bodyCellSx}>
                  {formatDateRange(
                    pkg.fieldStartDate,
                    pkg.plannedFieldFinishDate
                  )}
                </TableCell>
              )}
              <TableCell align="right" sx={bodyCellSx}>
                {formatHours(pkg.shopHrsBudgeted)}
              </TableCell>
              <TableCell align="right" sx={bodyCellSx}>
                {formatHours(pkg.fieldHrsBudgeted)}
              </TableCell>
              <TableCell align="right" sx={bodyCellSx}>
                {formatHours(pkg.totalHrsBudgeted)}
              </TableCell>
              {showActualHours && (
                <TableCell align="right" sx={bodyCellSx}>
                  {formatHours(pkg.actualHrs)}
                </TableCell>
              )}
              {showVariance && (
                <>
                  <TableCell
                    align="right"
                    sx={{
                      ...bodyCellSx,
                      color: getVarianceColor(pkg.varianceHrs),
                      fontWeight: 600,
                    }}
                  >
                    {formatHours(pkg.varianceHrs)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      ...bodyCellSx,
                      color: getVarianceColor(pkg.varianceHrs),
                      fontWeight: 600,
                    }}
                  >
                    {formatVariancePct(pkg.variancePct)}
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default WorkPackageProgressTable;
